import React, { useState } from "react";
import * as finService from "../../services/finService";
import useModuleAccess from "../../hooks/useModuleAccess";
import { isModuleAdmin } from "../../constants/platformModules";
import useGlContext from "./gl/glContext";
import { Badge, GlState, LedgerBar, act, hint } from "./gl/glUi";
import { AppDropdown, EmptyState, LoadState, PageHeader, PrimaryButton, cardStyle, formatDate, pageStyle, tdStyle, thStyle, useLoad } from "../../components/ui";

// Accounting periods: each subledger (GL, receivables, payables, cash, inventory, dealership,
// fixed assets) opens and closes its own periods. Reopening a closed period needs approval.
const APPS = [
  ["GL", "General ledger"],
  ["AR", "Receivables"],
  ["AP", "Payables"],
  ["CASH", "Cash"],
  ["INV", "Inventory"],
  ["DMS", "Dealership"],
  ["FA", "Assets"],
];
const NEXT = {
  NEVER_OPENED: [
    ["OPEN", "Open"],
    ["FUTURE", "Future entry"],
  ],
  FUTURE: [["OPEN", "Open"]],
  OPEN: [["CLOSED", "Close"]],
  CLOSED: [
    ["OPEN", "Reopen (needs approval)"],
    ["PERMANENTLY_CLOSED", "Close permanently"],
  ],
  PERMANENTLY_CLOSED: [],
};

function Cell({ status, admin, onSet }) {
  const choices = admin ? NEXT[status] || [] : [];
  return (
    <div className="d-flex flex-column align-items-start gap-1">
      <Badge status={status} />
      {choices.length ? (
        <select
          className="form-select form-select-sm"
          style={{ fontSize: 12, width: "auto", minWidth: 110 }}
          value=""
          aria-label="Change status"
          onChange={(e) => {
            const to = e.target.value;
            if (!to) return;
            if (to === "PERMANENTLY_CLOSED" && !window.confirm("A permanently closed period can never be reopened. Continue?")) return;
            onSet(to);
          }}
        >
          <option value="">Change…</option>
          {choices.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}

function Periods({ gl, admin }) {
  const [year, setYear] = useState(null);
  const all = useLoad(() => finService.listPeriods(gl.ledger.id), [gl.ledger.code]);
  const years = [...new Set((all.data || []).map((p) => p.fiscal_year))].sort();
  const current = year || years[years.length - 1] || null;
  const rows = (all.data || []).filter((p) => p.fiscal_year === current);

  const set = async (p, app, status) => (await act(() => finService.setPeriodStatus(gl.ledger.id, p.id, app, status))) && all.reload();
  const addYear = async () => {
    const next = years.length ? Math.max(...years) + 1 : new Date().getFullYear();
    if (!window.confirm(`Add the periods for fiscal year ${next}?`)) return;
    if (await act(() => finService.addFiscalYear(gl.ledger.calendar_id, next))) {
      setYear(next);
      all.reload();
    }
  };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <div style={{ width: 160 }}>
          <AppDropdown id="fy" value={current} options={years.map((y) => ({ label: `Fiscal year ${y}`, value: y }))} onChange={(e) => setYear(e.value)} placeholder="Fiscal year" />
        </div>
        {admin ? <PrimaryButton onClick={addYear}>Add fiscal year</PrimaryButton> : null}
        <span style={hint}>Close the general ledger last, once every subledger for the period is closed.</span>
      </div>
      <LoadState loading={all.loading} error={all.error} onRetry={all.reload}>
        {rows.length === 0 ? (
          <EmptyState title="No periods" text="Add a fiscal year to create its periods." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 1000 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Period</th>
                  <th style={thStyle}>Dates</th>
                  {APPS.map(([k, l]) => (
                    <th key={k} style={thStyle}>
                      {l}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.period_name}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>
                      {p.period_name}
                      {p.is_adjusting ? <div style={hint}>Adjusting</div> : null}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap", fontSize: 13 }}>
                      {formatDate(p.start_date)} – {formatDate(p.end_date)}
                    </td>
                    {APPS.map(([k]) => (
                      <td key={k} style={tdStyle}>
                        <Cell status={p.statuses[k]} admin={admin} onSet={(to) => set(p, k, to)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
    </>
  );
}

export default function FinPeriods() {
  const gl = useGlContext();
  const { access } = useModuleAccess();
  const admin = isModuleAdmin(access, "FIN");
  return (
    <div style={pageStyle}>
      <PageHeader title="Periods" subtitle="Open and close accounting periods" />
      <GlState gl={gl}>
        <LedgerBar gl={gl} />
        <Periods key={gl.ledger && gl.ledger.code} gl={gl} admin={admin} />
      </GlState>
    </div>
  );
}
